import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { Close, Undo, WarningAmberRounded } from '@mui/icons-material';
import { format, isAfter, isBefore, startOfDay } from 'date-fns';
import { LeaveRequest } from '../../services/leaveService';

interface RevokeLeaveDialogProps {
  open: boolean;
  request: LeaveRequest | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
}

/**
 * Revoke an APPROVED leave. Distinct from rejection — this undoes an
 * approval that was already granted, refunding the employee's `used`
 * balance on the server side. Designed for the rare-but-real case of
 * "I approved this by mistake / circumstances changed."
 *
 * The dialog surfaces the in-progress / past-leave warning explicitly
 * because revoking a leave that the employee has already taken is
 * semantically weird (they were absent on those days) and the admin
 * should consciously acknowledge it.
 */
const RevokeLeaveDialog: React.FC<RevokeLeaveDialogProps> = ({
  open,
  request,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!request) return null;

  const employeeName = typeof request.employeeId === 'object' ? request.employeeId.name : 'Unknown';
  const leaveTypeName = typeof request.leaveTypeId === 'object' ? request.leaveTypeId.name : 'Unknown';
  const start = new Date(request.startDate);
  const end = new Date(request.endDate);
  const today = startOfDay(new Date());
  const inProgress = !isAfter(start, today) && !isBefore(end, today);
  const inPast = isBefore(end, today);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: theme.palette.warning.main,
            }}
          >
            <Undo fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={600}>Revoke Approved Leave</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={loading}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Card variant="outlined" sx={{ mb: 2, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
          <CardContent sx={{ py: 1.75 }}>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Employee</Typography>
                <Typography variant="body2" fontWeight={600}>{employeeName}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="caption" color="text.secondary">Leave Type</Typography>
                <Typography variant="body2" fontWeight={600}>{leaveTypeName}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">Approved Dates</Typography>
                <Typography variant="body2">
                  {format(start, 'MMM dd, yyyy')} – {format(end, 'MMM dd, yyyy')}
                  {' • '}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {request.totalDays} day{request.totalDays > 1 ? 's' : ''}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Alert
          severity={inPast || inProgress ? 'warning' : 'info'}
          icon={<WarningAmberRounded fontSize="small" />}
          sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
        >
          {inPast && (
            <>This leave has <strong>already been taken</strong>. Revoking it will refund the {request.totalDays} day{request.totalDays > 1 ? 's' : ''} to the employee's balance, but their attendance for those dates will remain whatever it was marked as.</>
          )}
          {inProgress && !inPast && (
            <>This leave is <strong>currently in progress</strong>. Revoking it will refund the days to the employee's balance.</>
          )}
          {!inPast && !inProgress && (
            <>The {request.totalDays} day{request.totalDays > 1 ? 's' : ''} will be refunded to the employee's <strong>{leaveTypeName}</strong> balance. The request will be marked as <strong>Revoked</strong> in the history.</>
          )}
        </Alert>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          label="Reason for revocation (recommended)"
          placeholder="e.g. Approved in error / employee no longer needs this leave"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Keep Approval
        </Button>
        <Button
          onClick={() => onConfirm(reason.trim())}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Undo />}
        >
          {loading ? 'Revoking…' : 'Revoke Leave'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevokeLeaveDialog;
